import type { CommentItem, CommentsList, PostContext, ReactionSummary } from '../types';

export interface UploadedMedia {
  media_file_id: string;
  media_type: string;
}

export function mediaUrl(mediaFileId: string): string {
  return `/api/media/${mediaFileId}`;
}

const INIT_DATA_HEADER = 'X-Init-Data';

function getInitData(): string {
  const app = window.WebApp;
  return app?.initData ?? '';
}

/**
 * В MAX mini-app параметр приходит через `startapp=c_<channel_id>_<post_mid_short>`.
 * MAX API не допускает точки в OpenAppButton.payload, поэтому бот вырезает префикс
 * "mid." из post_mid. Здесь возвращаем его обратно.
 */
const POST_MID_PREFIX = 'mid.';

export function parsePostContext(startParam: string | undefined): PostContext | null {
  if (!startParam) return null;
  if (!startParam.startsWith('c_')) return null;
  const rest = startParam.slice(2);
  const idx = rest.indexOf('_');
  if (idx <= 0) return null;
  const channelIdStr = rest.slice(0, idx);
  const postMidShort = rest.slice(idx + 1);
  const channelId = Number(channelIdStr);
  if (!Number.isFinite(channelId) || !postMidShort) return null;
  const postMid = postMidShort.startsWith(POST_MID_PREFIX)
    ? postMidShort
    : POST_MID_PREFIX + postMidShort;
  return { channelId, postMid };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      [INIT_DATA_HEADER]: getInitData(),
    },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(path, init);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let detail: string | undefined;
    try {
      detail = JSON.parse(text)?.detail;
    } catch {
      detail = text || undefined;
    }
    throw new ApiError(response.status, detail ?? response.statusText);
  }
  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function fetchVersion(ctx: PostContext): Promise<number> {
  const params = new URLSearchParams({
    channel: String(ctx.channelId),
    post: ctx.postMid,
  });
  const res = await request<{ version: number }>(
    'GET',
    `/api/comments/version?${params.toString()}`,
  );
  return res.version;
}

export async function fetchComments(
  ctx: PostContext,
  options: { limit?: number; offset?: number } = {},
): Promise<CommentsList> {
  const params = new URLSearchParams({
    channel: String(ctx.channelId),
    post: ctx.postMid,
    limit: String(options.limit ?? 50),
    offset: String(options.offset ?? 0),
  });
  return request<CommentsList>('GET', `/api/comments?${params.toString()}`);
}

export interface NewComment {
  text: string;
  replyToCommentId?: number;
  media?: UploadedMedia;
  asChannel?: boolean;
}

export async function postComment(
  ctx: PostContext,
  payload: NewComment,
): Promise<CommentItem> {
  return request<CommentItem>('POST', '/api/comments', {
    channel_id: ctx.channelId,
    post_mid: ctx.postMid,
    text: payload.text,
    reply_to_comment_id: payload.replyToCommentId ?? null,
    media_file_id: payload.media?.media_file_id ?? null,
    media_type: payload.media?.media_type ?? null,
    as_channel: payload.asChannel ?? false,
  });
}

export interface EditedComment {
  text: string;
  media?: UploadedMedia;
}

export async function editComment(
  commentId: number,
  payload: EditedComment,
): Promise<CommentItem> {
  return request<CommentItem>('POST', `/api/comments/${commentId}/edit`, {
    text: payload.text,
    media_file_id: payload.media?.media_file_id ?? null,
    media_type: payload.media?.media_type ?? null,
  });
}

export async function toggleReaction(
  commentId: number,
  emoji: string,
): Promise<ReactionSummary[]> {
  return request<ReactionSummary[]>('POST', `/api/comments/${commentId}/reactions`, {
    emoji,
  });
}

export async function uploadMedia(file: File): Promise<UploadedMedia> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch('/api/media', {
    method: 'POST',
    headers: { [INIT_DATA_HEADER]: getInitData() },
    body: form,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let detail: string | undefined;
    try {
      detail = JSON.parse(text)?.detail;
    } catch {
      detail = text || undefined;
    }
    throw new ApiError(response.status, detail ?? response.statusText);
  }
  return response.json() as Promise<UploadedMedia>;
}

export async function adminDeleteComment(commentId: number): Promise<CommentItem> {
  return request<CommentItem>(
    'POST',
    `/api/admin/comments/${commentId}/delete`,
  );
}

export async function adminMarkFalsePositive(logId: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    'POST',
    `/api/admin/moderation/${logId}/false_positive`,
  );
}
